/* eslint-disable @typescript-eslint/no-explicit-any */

import { ClientBusinessDefinedState, dashboard } from 'types';
import { type PartyStatusFormValues } from 'components/settings/business-defined-states/PartyStatusFormConfig';
import {
  convertClientObjectToServerObject,
  getPartyStatusColumn,
} from 'utils/businessDefinedStateUtils';
import { map } from 'es-toolkit/compat';
import AppConstants from 'constants/AppConstants';
import PartyStates from 'constants/partyStateConstants';
import protos from 'utils/protos';

const { BusinessDefinedState, BusinessDefinedStateList, TextingConfig } = protos;

export function fetchBusinessDefinedStatesByBusinessId(businessId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/${businessId}/service/config/state`,
      type: AppConstants.BUSINESS_DEFINED_STATE_LIST_FETCH,
    },
  };
}

export function fetchTextMessageConfigByBusinessId(businessId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/business/${businessId}/service/config/texting`,
      type: AppConstants.TEXT_MESSAGING_CONFIG_FETCH,
    },
  };
}

export function upsertTextMessageConfig(
  businessId: number,
  textingConfig: dashboard.TextingConfig
): ApiAction {
  return {
    TockApi: {
      putProto: `/business/${businessId}/service/config/texting`,
      type: AppConstants.TEXT_MESSAGING_UPSERT,
      request: TextingConfig.fromObject(textingConfig),
      toast: 'Texting configuration updated',
    },
  };
}

export function insertBusinessDefinedState(
  businessId: number,
  primaryState: string,
  color: any
): ApiAction {
  const jsonForProto = {
    color,
    primaryState,
    canDelete: true,
  };
  return {
    TockApi: {
      postListProto: `/business/${businessId}/service/config/state`,
      type: AppConstants.BUSINESS_DEFINED_STATE_INSERT,
      request: BusinessDefinedState.fromObject(jsonForProto),
      context: { primaryState },
      toast: 'Party status created',
    },
  };
}

// Used for the new party status add/edit modal. The original action doesn't support all values.
export function insertBusinessDefinedStateFromFormValues(
  businessId: number,
  primaryState: string,
  values: PartyStatusFormValues
): ApiAction {
  const jsonForProto = {
    primaryState,
    canDelete: true,
    secondaryState: values.secondaryState,
    pantryColor: values.pantryColor,
    color: values.pantryColor,
    icon: values.icon,
    border: values.hasDashedBorder ? 'DASHED' : 'NONE',
  };
  return {
    TockApi: {
      postListProto: `/business/${businessId}/service/config/state`,
      type: AppConstants.BUSINESS_DEFINED_STATE_INSERT,
      request: BusinessDefinedState.fromObject(jsonForProto),
      context: { primaryState },
      toast: 'Party status created',
    },
  };
}

export function updateBusinessDefinedState(
  businessDefinedState: ClientBusinessDefinedState,
  businessId: number
): ApiAction {
  const request = BusinessDefinedState.fromObject(
    convertClientObjectToServerObject(businessDefinedState)
  );
  return {
    TockApi: {
      putListProto: `/business/${businessId}/service/config/state`,
      type: AppConstants.BUSINESS_DEFINED_STATE_UPDATE,
      request,
      context: { primaryState: businessDefinedState.primaryState },
      toast: 'Party status updated',
    },
  };
}

export function updateBusinessDefinedStateList(
  businessDefinedState: dashboard.BusinessDefinedState[],
  businessId: number,
  updatingState: dashboard.BusinessDefinedState
): ApiAction {
  const request = BusinessDefinedStateList.fromObject({
    businessDefinedState: map(businessDefinedState, (state) =>
      BusinessDefinedState.fromObject(state)
    ),
  });
  const primaryStateAreaUpdating =
    getPartyStatusColumn(updatingState.primaryState) === 'seated'
      ? PartyStates.seated
      : PartyStates.expected;
  return {
    TockApi: {
      putListProto: `/business/${businessId}/service/config/state/list`,
      type: AppConstants.BUSINESS_DEFINED_STATE_UPDATE,
      request,
      context: { primaryState: primaryStateAreaUpdating },
      toast: 'Party status updated',
    },
  };
}

export function deleteBusinessDefinedState(
  businessDefinedState: any,
  businessId: number
): ApiAction {
  return {
    TockApi: {
      deleteListProto: `/business/${businessId}/service/config/state/${businessDefinedState.id}`,
      type: AppConstants.BUSINESS_DEFINED_STATE_DELETE,
      context: { primaryState: businessDefinedState.primaryState },
      toast: 'Party status deleted',
    },
  };
}
